import type { PaymentMethodInfo } from '@/services/org-admin';

/**
 * Presenting the saved card.
 *
 * The three things the API tells us — brand, last four, expiry — and nothing derived from anywhere
 * else. Kept out of the panel so the expiry comparison can be unit-tested: it decides whether an
 * admin is told their card is dead, and an off-by-one month would say that about a working card.
 */

/**
 * Stripe's brand slugs, cased the way the brands write themselves.
 *
 * A plain `capitalize` was the obvious approach and is wrong twice over: it renders `unionpay` as
 * "Unionpay", and it renders Stripe's literal `unknown` as "Unknown", which reads like an error
 * rather than "a card we can't name". Unlisted slugs fall back to capitalisation, so a brand Stripe
 * adds later still renders sensibly instead of raw.
 */
const BRAND_LABEL: Record<string, string> = {
  amex: 'Amex',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'Eftpos',
  jcb: 'JCB',
  link: 'Link',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

export function cardBrandLabel(brand: string): string {
  const key = brand.trim().toLowerCase();
  // Stripe's own placeholder. "Card" is what a person would call it; "Unknown" sounds like a fault.
  if (!key || key === 'unknown') return 'Card';
  return BRAND_LABEL[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

/** `MM/YYYY`, zero-padded so a column of these lines up. */
export function formatCardExpiry({ exp_month, exp_year }: PaymentMethodInfo): string {
  return `${String(exp_month).padStart(2, '0')}/${exp_year}`;
}

/**
 * Whether the card's expiry month has already passed.
 *
 * A card is valid through the LAST DAY of its expiry month, so a card marked 08/2026 works until the
 * 31st of August 2026 — comparing against a day-precision date would call it dead for a whole month
 * it is still good for. This compares whole months only, and `now` is a parameter so the boundary is
 * testable rather than dependent on when the suite runs.
 *
 * Returns false for a nonsense month (0, 13, NaN) rather than guessing: a malformed expiry is not
 * evidence that the card is expired, and warning on it would be a false alarm about real money.
 */
export function isCardExpired(card: PaymentMethodInfo, now: Date = new Date()): boolean {
  const { exp_month, exp_year } = card;
  if (!Number.isInteger(exp_month) || exp_month < 1 || exp_month > 12) return false;
  if (!Number.isInteger(exp_year) || exp_year < 1970) return false;

  const nowMonths = now.getFullYear() * 12 + now.getMonth(); // getMonth() is 0-based
  const cardMonths = exp_year * 12 + (exp_month - 1);
  return cardMonths < nowMonths;
}
